import { useRef, useEffect, useCallback } from 'react';

export function useThrottle<CallbackParams extends unknown[]>(
  callback: (...args: CallbackParams) => void,
  wait = 0,
): (...args: CallbackParams) => void {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  const prev = useRef(0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const clearRunTimeout = () => timeoutRef.current && clearTimeout(timeoutRef.current);

  // Reset any time the deps change
  useEffect(
    () => () => {
      prev.current = 0;
      clearRunTimeout();
    },
    [wait, callbackRef],
  );

  return useCallback(
    (...args: CallbackParams) => {
      const rightNow = Date.now();

      const call = () => {
        prev.current = rightNow;
        clearRunTimeout();
        callbackRef.current.apply(null, args);
      };
      const current = prev.current;

      if (rightNow - current > wait) {
        if (current > 0) {
          return call();
        }
        prev.current = rightNow;
      }

      clearRunTimeout();
      timeoutRef.current = setTimeout(() => {
        call();
        prev.current = 0;
      }, wait);
    },
    [wait, callbackRef],
  );
}
